// Whole-document photo attachments (backlog DOCS-20). Client-side
// compression only — this app has no server-side image processing
// anywhere, and egress (not storage) was flagged as the real cost driver
// for this feature when it was scoped, so a smaller re-encode keeps both
// down with no visible quality loss on a printed page. The HEIC fallback
// below (DOCS-37) stays client-side too, on purpose — see its own comment.
import { supabase } from "@/lib/supabase";
import { requireAccountId } from "@/lib/profile";
import type { DocPhoto } from "@/lib/types";

const BUCKET = "account-files";
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.8;
const SIGNED_URL_TTL_SECONDS = 60 * 60; // 1 hour — regenerated fresh at every render, never persisted
const LIST_PAGE_SIZE = 100;

/**
 * DOCS-32: moved here from photo-attachments.tsx (a UI component) — this is
 * the Storage-domain home for this feature's constants, and saveDocument()
 * (src/lib/documents.ts) needs the same value to enforce it server-side,
 * which a `src/lib` file importing from a component would get backwards.
 */
export const MAX_PHOTOS = 8;

/**
 * Thrown when the browser can't decode the given file as an image at all —
 * distinct from a network/Storage failure so the UI can tell "this format
 * isn't supported here" apart from "try again." The common real case
 * (DOCS-37): Chrome has no native HEIC/HEIF decoder on any platform,
 * including Android, unlike Safari/iOS which transcodes HEIC to JPEG
 * automatically before a web page ever sees the file — so a HEIC photo
 * forwarded to an Android/Chrome user (e.g. a client's iPhone photo) can
 * reach this app un-converted and fail here.
 */
export class UnsupportedImageError extends Error {}

/** Resizes to a max long-edge of MAX_DIMENSION and re-encodes as JPEG. Never upscales a smaller source image. */
export async function compressImage(file: File): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new UnsupportedImageError(`Could not decode "${file.name}" as an image.`);
  }
  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get a 2D canvas context to compress this image.");
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Could not encode the compressed image."))),
      "image/jpeg",
      JPEG_QUALITY,
    );
  });
}

/**
 * Fallback for a file `compressImage` can't decode natively — in practice,
 * almost always HEIC/HEIF on Android/Chrome (see `UnsupportedImageError`
 * above). Deliberately client-side (the `heic-to` package, dynamically
 * imported so its ~700KB doesn't touch the main bundle for the overwhelming
 * majority of photos that never need it): a server round-trip would pay a
 * multi-MB upload-then-download cost on every conversion with no caching
 * possible, which is a real risk on the uncertain job-site cellular this
 * app is built for — a one-time local decoder download, then a purely
 * local decode, doesn't have that failure mode. If real-device testing
 * ever finds this unreliable or too slow on Chrome/Android, the documented
 * next step (see the DOCS-37 plan) is a server-side fallback behind this
 * same call, not a rewrite of the call site.
 *
 * Re-throws as `UnsupportedImageError` on failure so the caller's existing
 * `instanceof` check keeps working — this only fires for a file that's
 * genuinely not a decodable image at all (heic-to failed too).
 */
async function convertUnsupportedPhoto(file: File): Promise<Blob> {
  const { heicTo } = await import("heic-to");
  try {
    return await heicTo({ blob: file, type: "image/jpeg", quality: JPEG_QUALITY });
  } catch {
    throw new UnsupportedImageError(`Could not decode "${file.name}" as an image.`);
  }
}

/**
 * Compresses and uploads one photo, returning a DocPhoto ready to append to
 * a document's `photos` array. `url` is a bucket-relative path, not a
 * usable URL — see resolvePhotoUrl. Keyed by a client-generated id, not the
 * document's docKey: a brand-new quote has no docKey until first save, and
 * a storage object never actually needs to know which document (if any)
 * ends up referencing it.
 */
export async function uploadDocPhoto(file: File, onConverting?: () => void): Promise<DocPhoto> {
  const accountIdPromise = requireAccountId();
  let compressed: Blob;
  try {
    compressed = await compressImage(file);
  } catch (e) {
    if (!(e instanceof UnsupportedImageError)) throw e;
    // Real-device testing (DOCS-37, Galaxy A10e) confirmed this can take
    // ~20+ seconds on older hardware — the app stays responsive throughout
    // (heic-to decodes off the main thread) but a generic "Uploading…" for
    // that long looks broken. `onConverting` lets the caller show something
    // honest about why this one photo is taking longer than usual.
    onConverting?.();
    const converted = await convertUnsupportedPhoto(file);
    compressed = await compressImage(new File([converted], file.name, { type: "image/jpeg" }));
  }
  const accountId = await accountIdPromise;
  const id = crypto.randomUUID();
  const path = `${accountId}/doc-photos/${id}.jpg`;

  const { error } = await supabase.storage.from(BUCKET).upload(path, compressed, {
    contentType: "image/jpeg",
    upsert: false,
  });
  if (error) throw error;

  return { id, url: path, caption: "" };
}

/**
 * Resolves a stored path to a real, viewable URL. The bucket is private,
 * so a plain public URL never works, and a signed URL generated once at
 * upload time would go stale if the document is viewed weeks later —
 * always regenerate at render time instead of persisting the result.
 */
export async function resolvePhotoUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
  if (error) throw error;
  return data.signedUrl;
}

/**
 * Total bytes across every object under account-files/{accountId}/ for the
 * *current* account — DOCS-30: getStorageStats() (src/lib/documents.ts)
 * previously counted zero bytes of this bucket. Structurally the same
 * recursive list()-with-sizes algorithm as
 * supabase/functions/_shared/accountFiles.ts's listAllEntries() — that one
 * can't be imported here (Deno vs. browser module systems), so this is the
 * accepted duplication, same reasoning as this codebase's existing
 * TRIAL_DAYS/GRACE_DAYS mirroring across src/lib/config.ts and the Edge
 * Functions that can't import it.
 */
export async function getAccountFilesBytes(): Promise<number> {
  const accountId = await requireAccountId();
  return await sumFolderBytes(accountId);
}

async function sumFolderBytes(prefix: string): Promise<number> {
  let total = 0;
  let offset = 0;
  for (;;) {
    const { data, error } = await supabase.storage.from(BUCKET).list(prefix, { limit: LIST_PAGE_SIZE, offset });
    if (error) throw error;
    if (!data || data.length === 0) break;
    for (const entry of data) {
      const entryPath = `${prefix}/${entry.name}`;
      if (entry.id === null) {
        total += await sumFolderBytes(entryPath);
      } else {
        total += (entry.metadata as { size?: number } | null)?.size ?? 0;
      }
    }
    if (data.length < LIST_PAGE_SIZE) break;
    offset += LIST_PAGE_SIZE;
  }
  return total;
}
